import { Component, ElementRef, TemplateRef, ViewChild } from "@angular/core";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Helper } from "src/app/core/helpers/helper";
import { PaymentService } from "../../services/payment.service";
import {
  CreditStatus,
  CreditTransactionType,
  PenaltyType,
  CreditStatusString,
  PenaltyTypeString,
  CreditTransactionTypeString,
  RefundReason,
  RefundReasonString
} from "src/app/core/constants/walletConstant";

@Component({
  selector: "app-wallet-history-modal",
  templateUrl: "./wallet-history-modal.component.html",
  styleUrl: "./wallet-history-modal.component.scss",
  standalone: false
})
export class WalletHistoryModalComponent {
  page: number = 1;
  limit: number = 15;
  pageCount: number = 1;
  isCreditHistoryLoading: boolean = false;
  creditHistory: any = [];
  totalWalletAmount: number = 0;

  CreditStatus = CreditStatus;
  CreditTransactionType = CreditTransactionType;
  PenaltyType = PenaltyType;
  CreditStatusString = CreditStatusString;
  PenaltyTypeString = PenaltyTypeString;
  CreditTransactionTypeString = CreditTransactionTypeString;

  @ViewChild("modal", { static: true }) modal!: TemplateRef<any>;
  @ViewChild("scrollContainerElement", { static: true }) scrollContainerRef!: ElementRef;

  constructor(public offcanvasService: NgbOffcanvas, public helper: Helper, private paymentService: PaymentService) {}

  /**
   * Opens the wallet history modal and initializes the wallet history data.
   */
  openModal(): void {
    this.getWalletHistory();
    this.offcanvasService.open(this.modal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" })?.result.then(
      () => this.closeModal(),
      () => this.closeModal()
    );
  }

  /**
   * Fetches the wallet history data from the server.
   * If fromScroll is true, appends the data to the existing history.
   * Otherwise, resets the history and fetches the first page of data.
   *
   * @param fromScroll Indicates if the method is called from a scroll event.
   */
  getWalletHistory(fromScroll: boolean = false): void {
    if (!fromScroll) {
      this.creditHistory = [];
      this.page = 1;
      this.limit = 15;
    }

    // Avoid loading more data if already loading or if all wallet history are loaded
    if (this.isCreditHistoryLoading || this.page > this.pageCount) {
      return;
    }
    this.isCreditHistoryLoading = true;

    const params = {
      limit: this.limit,
      page: this.page,
      export: false
    };
    this.paymentService.transactionHistory(params).subscribe((response: any) => {
      if (response) {
        const transactions: any = this.handleDescription(response.transactions);
        this.creditHistory.push(...transactions);
        this.totalWalletAmount = this.creditHistory.length ? this.creditHistory[0].totalWalletAmount : 0;
        this.pageCount = Math.ceil(Number(response.dataCount) / this.limit);
        this.page++;
      }

      this.isCreditHistoryLoading = false;
    });

    setTimeout(() => {
      const scrollContainer = document.getElementById("scrollContainerElement");
      if (scrollContainer) {
        scrollContainer.addEventListener("scroll", this.onScroll.bind(this));
      }
    }, 100);
  }

  /**
   * Processes the transaction descriptions based on their type.
   *
   * @param transactions The list of transactions to process.
   */
  handleDescription(transactions: any): void {
    transactions.forEach((transaction: any) => {
      switch (transaction.transactionType) {
        case this.CreditTransactionType.FROM_ADMIN:
          transaction.descriptionTitle = this.helper.translateService.instant(this.CreditTransactionTypeString[transaction.transactionType]);
          break;
        case this.CreditTransactionType.PENALTY:
          transaction.descriptionTitle = this.helper.translateService.instant(this.PenaltyTypeString[transaction.description]);
          break;
        case this.CreditTransactionType.DIGITAL_PAYMENT:
          transaction.descriptionTitle = `${this.helper.translateService.instant(
            this.CreditTransactionTypeString[transaction.transactionType]
          )} : ${this.helper.translateService.instant(this.helper.PaymentGatewayString[transaction.description])}`;
          break;
        case this.CreditTransactionType.REFUND:
          transaction.descriptionTitle = this.helper.translateService.instant(this.CreditTransactionTypeString[transaction.transactionType]);
          transaction.descriptionTitle += ` : ${transaction.bookingUniqueId}`;
          if (transaction.description === RefundReason.BOOKING_REMAINING_AMOUNT) {
            transaction.descriptionTitle += `<br> ${this.helper.translateService.instant("label-title.refund-reason")}  :  ${this.helper.translateService.instant(
              RefundReasonString[transaction.description]
            )}`;
          } else {
            transaction.descriptionTitle += `<br> ${this.helper.translateService.instant("label-title.refund-reason")}  :  ${transaction.description}`;
          }
          break;
        default:
          transaction.descriptionTitle = `${this.helper.translateService.instant(this.CreditTransactionTypeString[transaction.transactionType])} : ${transaction.description}`;
          break;
      }
    });

    return transactions;
  }

  /**
   * Handles the scroll event to load more wallet history data when the user scrolls to the bottom.
   */
  onScroll(): void {
    const scrollContainer = document.getElementById("scrollContainerElement");
    if (scrollContainer) {
      const distanceFromBottom = scrollContainer.scrollHeight - (scrollContainer.scrollTop + scrollContainer.clientHeight);

      if (distanceFromBottom < 1 && !this.isCreditHistoryLoading) {
        this.getWalletHistory(true);
      }
    }
  }

  /**
   * Closes the wallet history modal and resets the wallet history data.
   */
  closeModal(): void {
    this.offcanvasService?.dismiss(this.modal);
    this.creditHistory = [];
    this.isCreditHistoryLoading = false;
    this.page = 1;
    this.pageCount = 1;
  }
}
